import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft,
  Pencil,
  X,
  Plus,
  Trash2,
  Check,
  AlertCircle,
  AlertTriangle,
  FileText,
  Tag,
  Database,
} from 'lucide-react';
import {
  UpdatePlanMatrizSchema,
  ROLES,
  type UpdatePlanMatriz,
} from '@sime/shared';
import {
  usePlanMatriz,
  useUpdatePlanMatriz,
  useCreatePlanMatrizAlias,
  useDeletePlanMatrizAlias,
  usePlanMatrizTickets,
} from '@/api/plan-matriz';
import { usePautas, useCatalogoEco } from '@/api/catalogos';
import { useAuthStore } from '@/stores/auth.store';
import { NeuCard } from '@/components/ui/neu-card';
import { NeuButton } from '@/components/ui/neu-button';
import { NeuInput } from '@/components/ui/neu-input';
import { NeuSelect } from '@/components/ui/neu-select';
import { NeuBadge } from '@/components/ui/neu-badge';
import { formatDate } from '@/lib/format';
import { cn } from '@/lib/utils';

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="py-2">
      <p className="text-xs text-sime-text-tertiary">{label}</p>
      <p className="text-sm text-sime-text-primary font-medium">{value}</p>
    </div>
  );
}

export function PlanMatrizDetailPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const planMatrizId = Number(id);
  const user = useAuthStore((s) => s.user);
  const canEdit = user?.tipoUsuarioId === ROLES.ADMINISTRADOR;

  const [isEditing, setIsEditing] = useState(false);
  const [newAliasName, setNewAliasName] = useState('');
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
  const [selectedPautaId, setSelectedPautaId] = useState<number | undefined>(undefined);
  const [selectedEcoId, setSelectedEcoId] = useState<number | null | undefined>(undefined);

  const { data: detailData, isLoading, isError } = usePlanMatriz(planMatrizId);
  const { data: ticketsData } = usePlanMatrizTickets(planMatrizId);
  const { data: pautas = [] } = usePautas();

  const ambitoId = detailData?.data.ambito?.id;
  const { data: catalogoEco = [] } = useCatalogoEco(ambitoId);
  const updateMutation = useUpdatePlanMatriz(planMatrizId);
  const createAliasMutation = useCreatePlanMatrizAlias(planMatrizId);
  const deleteAliasMutation = useDeletePlanMatrizAlias(planMatrizId);

  const planMatriz = detailData?.data;
  const tickets = ticketsData?.data ?? [];

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<UpdatePlanMatriz>({
    resolver: zodResolver(UpdatePlanMatrizSchema),
  });

  // Populate form when data loads
  useEffect(() => {
    if (planMatriz) {
      reset({
        nombre: planMatriz.nombre,
        codigo: planMatriz.codigo ?? undefined,
        frecuencia: planMatriz.frecuencia ?? undefined,
        tiempoTrabajo: planMatriz.tiempoTrabajo ?? undefined,
        tiempoViaje: planMatriz.tiempoViaje ?? undefined,
      });
      setSelectedPautaId(planMatriz.pautasId ?? undefined);
      setSelectedEcoId(planMatriz.catalogoeco?.id ?? null);
    }
  }, [planMatriz, reset]);

  const handleSave = useCallback(
    async (data: UpdatePlanMatriz) => {
      try {
        await updateMutation.mutateAsync({ ...data, pautasId: selectedPautaId, catalogoecoId: selectedEcoId });
        setIsEditing(false);
      } catch (err) {
        // TanStack Query captures this in mutation.error for UI display
        console.error('Mutation failed:', err);
      }
    },
    [updateMutation, selectedPautaId, selectedEcoId],
  );

  const handleCancelEdit = useCallback(() => {
    if (planMatriz) {
      reset({
        nombre: planMatriz.nombre,
        codigo: planMatriz.codigo ?? undefined,
        frecuencia: planMatriz.frecuencia ?? undefined,
        tiempoTrabajo: planMatriz.tiempoTrabajo ?? undefined,
        tiempoViaje: planMatriz.tiempoViaje ?? undefined,
      });
      setSelectedPautaId(planMatriz.pautasId ?? undefined);
      setSelectedEcoId(planMatriz.catalogoeco?.id ?? null);
    }
    setIsEditing(false);
  }, [planMatriz, reset]);

  const handleAddAlias = useCallback(async () => {
    const trimmed = newAliasName.trim();
    if (!trimmed) return;
    try {
      await createAliasMutation.mutateAsync({ nombre: trimmed });
      setNewAliasName('');
    } catch (err) {
      // TanStack Query captures this in mutation.error for UI display
      console.error('Mutation failed:', err);
    }
  }, [newAliasName, createAliasMutation]);

  const handleDeleteAlias = useCallback(
    async (aliasId: number) => {
      try {
        await deleteAliasMutation.mutateAsync(aliasId);
        setDeleteConfirmId(null);
      } catch (err) {
        // TanStack Query captures this in mutation.error for UI display
        console.error('Mutation failed:', err);
      }
    },
    [deleteAliasMutation],
  );

  // Loading skeleton
  if (isLoading) {
    return (
      <div className="max-w-[1200px] mx-auto">
        <div className="flex items-center gap-4 mb-8">
          <div className="h-10 w-10 bg-sime-surface-raised rounded-xl animate-pulse" />
          <div className="h-8 w-64 bg-sime-surface-raised rounded animate-pulse" />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <div className="h-48 bg-sime-surface-raised rounded-2xl animate-pulse" />
            <div className="h-32 bg-sime-surface-raised rounded-2xl animate-pulse" />
          </div>
          <div className="h-64 bg-sime-surface-raised rounded-2xl animate-pulse" />
        </div>
      </div>
    );
  }

  if (isError || !planMatriz) {
    return (
      <div className="max-w-[1200px] mx-auto text-center py-20">
        <AlertCircle className="h-12 w-12 mx-auto mb-4 text-sime-text-tertiary" />
        <p className="text-sime-text-secondary font-medium mb-4">
          No se pudo cargar el plan matriz.
        </p>
        <NeuButton variant="secondary" onClick={() => { void navigate('/plan-matriz'); }}>
          Volver a Plan Matriz
        </NeuButton>
      </div>
    );
  }

  return (
    <div className="max-w-[1200px] mx-auto px-4 md:px-6 lg:px-0">
      {/* Breadcrumb + Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between mb-6 md:mb-8">
        <div className="flex items-center gap-3 md:gap-4">
          <button
            onClick={() => { void navigate('/plan-matriz'); }}
            className="p-2 rounded-xl text-sime-text-secondary hover:text-sime-text-primary transition-colors"
            aria-label="Volver a plan matriz"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <nav aria-label="Breadcrumb" className="mb-1">
              <ol className="flex items-center gap-1.5 text-xs text-sime-text-tertiary">
                <li>
                  <button
                    onClick={() => { void navigate('/plan-matriz'); }}
                    className="hover:text-sime-text-secondary transition-colors"
                  >
                    Plan Matriz
                  </button>
                </li>
                <li aria-hidden="true">/</li>
                <li className="text-sime-text-secondary font-medium truncate max-w-[300px]">
                  {planMatriz.nombre}
                </li>
              </ol>
            </nav>
            <div className="flex items-center gap-3">
              <h1 className="text-xl md:text-[28px] font-bold text-sime-text-primary tracking-tight-md truncate max-w-[260px] md:max-w-[500px]">
                {planMatriz.nombre}
              </h1>
              {planMatriz.activo ? (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-sime-success/15 text-sime-success">
                  <Check className="h-3 w-3" />
                  Activo
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-sime-danger/15 text-sime-danger">
                  <X className="h-3 w-3" />
                  Inactivo
                </span>
              )}
            </div>
          </div>
        </div>

        {canEdit && !isEditing ? (
          <NeuButton
            variant="secondary"
            size="md"
            onClick={() => { setIsEditing(true); }}
          >
            <Pencil className="h-4 w-4 mr-2" />
            Editar
          </NeuButton>
        ) : null}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Detail / Edit Form */}
          <NeuCard>
            {isEditing ? (
              <form onSubmit={(e) => void handleSubmit(handleSave)(e)} noValidate>
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-[17px] font-semibold text-sime-text-primary tracking-tight-xs">
                    Editar Informacion
                  </h2>
                  <div className="flex items-center gap-2">
                    <NeuButton
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={handleCancelEdit}
                      disabled={updateMutation.isPending}
                    >
                      Cancelar
                    </NeuButton>
                    <NeuButton
                      type="submit"
                      size="sm"
                      loading={updateMutation.isPending}
                      disabled={!isDirty}
                    >
                      Guardar
                    </NeuButton>
                  </div>
                </div>

                {updateMutation.isError ? (
                  <div role="alert" className="mb-4 rounded-xl p-3 text-sm text-sime-danger bg-sime-danger/10 border border-sime-danger/20">
                    Error al guardar los cambios. Intente nuevamente.
                  </div>
                ) : null}

                <div className="space-y-6">
                  <NeuInput
                    label="Nombre"
                    error={errors.nombre?.message}
                    {...register('nombre')}
                  />

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <NeuInput
                      label="Codigo"
                      error={errors.codigo?.message}
                      {...register('codigo')}
                    />
                    <NeuInput
                      label="Frecuencia"
                      error={errors.frecuencia?.message}
                      {...register('frecuencia')}
                    />
                  </div>

                  {/* Pauta selector */}
                  <div>
                    <NeuSelect
                      label="Pauta"
                      options={[
                        { value: '', label: '-- Sin pauta --' },
                        ...pautas.map((p) => ({ value: String(p.id), label: `${p.nombre} (${p.codigo})` })),
                      ]}
                      value={String(selectedPautaId ?? '')}
                      onChange={(v) => {
                        setSelectedPautaId(v ? Number(v) : undefined);
                      }}
                      error={!selectedPautaId ? ' ' : undefined}
                    />
                    {!selectedPautaId ? (
                      <p className="mt-1 text-xs text-sime-warning flex items-center gap-1">
                        <AlertTriangle className="h-3 w-3" />
                        Sin pauta asignada. Los tickets no se crearan en la carga Excel.
                      </p>
                    ) : null}
                  </div>

                  {/* ECO/BTU selector — visible when the catalog has vigentes for this
                      ámbito, OR the plan already points at an ECO (which may now be
                      inactive after a catalog curation: keep it selectable/visible so the
                      current value shows and isn't silently dropped on save). */}
                  {(catalogoEco.length > 0 || planMatriz.catalogoeco) ? (
                    <NeuSelect
                      label={(catalogoEco.some((c) => c.unidad === 'BTU') || planMatriz.catalogoeco?.unidad === 'BTU') ? 'BTU' : 'ECO'}
                      options={[
                        { value: '', label: '-- Sin asignar --' },
                        ...catalogoEco.map((c) => ({
                          value: String(c.id),
                          label: `${c.item} - ${c.eco}${c.unidad ? ` ${c.unidad}` : ''}`,
                        })),
                        // Plan's current ECO, if it's no longer in the vigente list.
                        ...(planMatriz.catalogoeco && !catalogoEco.some((c) => c.id === planMatriz.catalogoeco?.id)
                          ? [{
                              value: String(planMatriz.catalogoeco.id),
                              label: `${planMatriz.catalogoeco.item} - ${planMatriz.catalogoeco.eco}${planMatriz.catalogoeco.unidad ? ` ${planMatriz.catalogoeco.unidad}` : ''} (inactivo)`,
                            }]
                          : []),
                      ]}
                      value={String(selectedEcoId ?? '')}
                      onChange={(v) => {
                        setSelectedEcoId(v ? Number(v) : null);
                      }}
                    />
                  ) : null}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <NeuInput
                      label="Tiempo trabajo (hrs)"
                      type="number"
                      step="0.5"
                      error={errors.tiempoTrabajo?.message}
                      {...register('tiempoTrabajo', { valueAsNumber: true })}
                    />
                    <NeuInput
                      label="Tiempo viaje (hrs)"
                      type="number"
                      step="0.5"
                      error={errors.tiempoViaje?.message}
                      {...register('tiempoViaje', { valueAsNumber: true })}
                    />
                  </div>
                </div>
              </form>
            ) : (
              <>
                <h2 className="text-[17px] font-semibold text-sime-text-primary tracking-tight-xs mb-4">
                  Informacion General
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-1">
                  <InfoRow label="Nombre" value={planMatriz.nombre} />
                  <InfoRow label="Codigo" value={planMatriz.codigo ?? '-'} />
                  <InfoRow label="Ambito" value={planMatriz.ambito?.nombre ?? '-'} />
                  <InfoRow label="Subcontrato" value={planMatriz.subcontrato?.nombre ?? '-'} />
                  <InfoRow label="Empresa" value={planMatriz.empresa?.nombre ?? '-'} />
                  <InfoRow label="Ubicacion" value={planMatriz.ubicacion?.nombre ?? '-'} />
                  <InfoRow label="Tipo Mantenimiento" value={planMatriz.tipoMantenimiento?.nombre ?? '-'} />
                  <div className="py-2">
                    <p className="text-xs text-sime-text-tertiary">Pauta</p>
                    {planMatriz.pauta ? (
                      <p className="text-sm text-sime-text-primary font-medium">
                        {planMatriz.pauta.nombre}
                        <span className="text-sime-text-tertiary ml-1">({planMatriz.pauta.codigo})</span>
                      </p>
                    ) : (
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <AlertTriangle className="h-3.5 w-3.5 text-sime-warning" />
                        <span className="text-sm font-medium text-sime-warning">Sin pauta asignada</span>
                      </div>
                    )}
                  </div>
                  {planMatriz.catalogoeco ? (
                    <InfoRow
                      label={planMatriz.catalogoeco.unidad === 'BTU' ? 'BTU' : 'ECO'}
                      value={`${planMatriz.catalogoeco.item} - ${planMatriz.catalogoeco.eco}${planMatriz.catalogoeco.unidad ? ` ${planMatriz.catalogoeco.unidad}` : ''}`}
                    />
                  ) : null}
                  {planMatriz.tipoEquipo ? (
                    <InfoRow label="Tipo Equipo" value={planMatriz.tipoEquipo.detalle} />
                  ) : null}
                  <InfoRow label="Frecuencia" value={planMatriz.frecuencia ?? '-'} />
                  <InfoRow label="Tiempo trabajo" value={planMatriz.tiempoTrabajo != null ? `${planMatriz.tiempoTrabajo} hrs` : '-'} />
                  <InfoRow label="Tiempo viaje" value={planMatriz.tiempoViaje != null ? `${planMatriz.tiempoViaje} hrs` : '-'} />
                  <InfoRow label="Creado" value={formatDate(planMatriz.createdAt)} />
                  <InfoRow label="Actualizado" value={formatDate(planMatriz.updatedAt)} />
                </div>
              </>
            )}
          </NeuCard>

          {/* Aliases Section */}
          <NeuCard>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-[17px] font-semibold text-sime-text-primary tracking-tight-xs flex items-center gap-2">
                <Tag className="h-4 w-4 text-sime-text-tertiary" />
                Aliases
                <span className="text-xs font-normal text-sime-text-tertiary ml-1">
                  ({planMatriz.aliases.length})
                </span>
              </h2>
            </div>

            {/* Alias List */}
            {planMatriz.aliases.length > 0 ? (
              <ul className="space-y-2 mb-4" role="list" aria-label="Lista de aliases">
                {planMatriz.aliases.map((alias) => (
                  <li
                    key={alias.id}
                    className="flex items-center justify-between px-4 py-2.5 rounded-xl bg-sime-surface-raised group"
                  >
                    <span className="text-sm text-sime-text-primary truncate mr-4">
                      {alias.nombre}
                    </span>
                    {canEdit ? (
                      <AnimatePresence mode="wait">
                        {deleteConfirmId === alias.id ? (
                          <motion.div
                            key="confirm"
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.9 }}
                            className="flex items-center gap-1.5"
                          >
                            <button
                              type="button"
                              onClick={() => { void handleDeleteAlias(alias.id); }}
                              disabled={deleteAliasMutation.isPending}
                              className="px-2 py-1 rounded-lg text-xs font-medium text-white bg-sime-danger hover:opacity-90 transition-opacity disabled:opacity-50"
                              aria-label={`Confirmar eliminar alias ${alias.nombre}`}
                            >
                              {deleteAliasMutation.isPending ? 'Eliminando...' : 'Eliminar'}
                            </button>
                            <button
                              type="button"
                              onClick={() => { setDeleteConfirmId(null); }}
                              className="px-2 py-1 rounded-lg text-xs font-medium text-sime-text-secondary hover:text-sime-text-primary transition-colors"
                              aria-label="Cancelar eliminacion"
                            >
                              No
                            </button>
                          </motion.div>
                        ) : (
                          <motion.button
                            key="delete"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            type="button"
                            onClick={() => { setDeleteConfirmId(alias.id); }}
                            className="opacity-0 group-hover:opacity-100 p-1.5 rounded-lg text-sime-text-tertiary hover:text-sime-danger transition-all"
                            aria-label={`Eliminar alias ${alias.nombre}`}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </motion.button>
                        )}
                      </AnimatePresence>
                    ) : null}
                  </li>
                ))}
              </ul>
            ) : (
              <div className="text-center py-6 mb-4">
                <Tag className="h-8 w-8 mx-auto mb-2 text-sime-text-tertiary" />
                <p className="text-sm text-sime-text-tertiary">
                  No hay aliases definidos
                </p>
              </div>
            )}

            {deleteAliasMutation.isError ? (
              <div role="alert" className="mb-4 rounded-xl p-3 text-sm text-sime-danger bg-sime-danger/10 border border-sime-danger/20">
                Error al eliminar el alias. Intente nuevamente.
              </div>
            ) : null}

            {/* Add alias */}
            {canEdit ? (
              <div className="flex items-end gap-3">
                <div className="flex-1">
                  <label htmlFor="new-alias" className="text-sm font-medium text-sime-text-secondary mb-1.5 block">
                    Agregar alias
                  </label>
                  <input
                    id="new-alias"
                    type="text"
                    value={newAliasName}
                    onChange={(e) => { setNewAliasName(e.target.value); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void handleAddAlias(); } }}
                    placeholder="Nombre alternativo del equipo..."
                    maxLength={500}
                    className={cn(
                      'w-full rounded-xl px-4 py-3 bg-transparent text-sm',
                      'shadow-neu-btn-active',
                      'focus:ring-2 focus:ring-sime-accent focus:outline-none',
                      'placeholder:text-sime-text-tertiary text-sime-text-primary',
                    )}
                  />
                </div>
                <NeuButton
                  type="button"
                  size="md"
                  onClick={() => { void handleAddAlias(); }}
                  loading={createAliasMutation.isPending}
                  disabled={!newAliasName.trim()}
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Agregar
                </NeuButton>
              </div>
            ) : null}

            {createAliasMutation.isError ? (
              <div role="alert" className="mt-3 rounded-xl p-3 text-sm text-sime-danger bg-sime-danger/10 border border-sime-danger/20">
                Error al agregar el alias. Intente nuevamente.
              </div>
            ) : null}
          </NeuCard>
        </div>

        {/* Sidebar - Related Tickets */}
        <div>
          <NeuCard>
            <h2 className="text-[17px] font-semibold text-sime-text-primary tracking-tight-xs mb-4 flex items-center gap-2">
              <FileText className="h-4 w-4 text-sime-text-tertiary" />
              Tickets Recientes
            </h2>
            {tickets.length === 0 ? (
              <div className="text-center py-6">
                <Database className="h-8 w-8 mx-auto mb-2 text-sime-text-tertiary" />
                <p className="text-sm text-sime-text-tertiary">
                  Sin tickets vinculados
                </p>
              </div>
            ) : (
              <div className="space-y-0">
                {tickets.map((ticket, index) => (
                  <motion.button
                    key={ticket.id}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.03 }}
                    type="button"
                    onClick={() => { void navigate(`/tickets/${ticket.id}`); }}
                    className="w-full flex items-center justify-between px-3 py-3 rounded-xl hover:bg-sime-surface-raised transition-colors text-left"
                  >
                    <div>
                      <p className="text-sm font-medium text-sime-accent">
                        {ticket.correlativo}
                      </p>
                      <p className="text-xs text-sime-text-tertiary mt-0.5">
                        {ticket.periodo} &middot; {formatDate(ticket.createdAt)}
                      </p>
                    </div>
                    <NeuBadge estado={ticket.estadoOt} size="sm" />
                  </motion.button>
                ))}
              </div>
            )}
          </NeuCard>
        </div>
      </div>
    </div>
  );
}
